import { useCallback } from 'react';
import { useProjectStore } from '../store/projectStore';
import type {
  DesignSpec,
  SectionSpec,
  SectionType,
  ColorScheme,
  FontPair,
  LayoutStyle,
  DesignStyle,
} from '../types/design-spec';
import { generateId } from '../utils/helpers';

export function useDesignSpec() {
  const designSpec = useProjectStore((state) => state.config.designSpec);
  const updateDesignSpec = useProjectStore((state) => state.updateDesignSpec);

  const ensureDesignSpec = useCallback((): DesignSpec => {
    if (designSpec) return designSpec;

    const fallback: DesignSpec = {
      style: 'modern-minimalist',
      mood: 'Clean and professional',
      colors: {
        primary: '#1a56db',
        secondary: '#60a5fa',
        accent: '#f59e0b',
        background: '#ffffff',
        surface: '#f8fafc',
        text: '#1f2937',
        muted: '#6b7280',
        darkMode: {
          background: '#0f172a',
          surface: '#1e293b',
          text: '#f8fafc',
        },
      },
      typography: {
        headingFont: 'Inter',
        bodyFont: 'Noto Sans TC',
        baseSize: '16px',
        scale: 'medium',
      },
      layout: {
        maxWidth: '1280px',
        gridColumns: 12,
        spacing: 'normal',
        containerPadding: '1rem',
      },
      sections: [],
      components: {
        buttons: { style: 'solid', radius: '0.5rem', shadow: 'sm' },
        cards: { style: 'elevated', radius: '0.75rem' },
        forms: { style: 'rounded' },
      },
      animations: {
        pageLoad: 'fade-in 0.5s ease-out',
        scrollReveal: 'slide-up 0.6s ease-out',
        hover: 'scale 0.2s ease-in-out',
        transition: 'all 0.2s ease-in-out',
      },
      analysis: {
        model: 'deepseek-chat',
        confidence: 0.75,
        summary: '',
        referencesAnalyzed: 0,
      },
    };
    updateDesignSpec(fallback);
    return fallback;
  }, [designSpec, updateDesignSpec]);

  const setStyle = useCallback((style: DesignStyle) => {
    updateDesignSpec({ style });
  }, [updateDesignSpec]);

  const setColorScheme = useCallback((colorScheme: ColorScheme) => {
    updateDesignSpec({ colorScheme });
  }, [updateDesignSpec]);

  const setFontPair = useCallback((fontPair: FontPair) => {
    updateDesignSpec({ fontPair });
  }, [updateDesignSpec]);

  const setLayoutStyle = useCallback((layoutStyle: LayoutStyle) => {
    const current = designSpec ?? ensureDesignSpec();
    const maxWidth = layoutStyle === 'wide-multi' ? '1440px' : layoutStyle === 'centered-single' ? '1024px' : current.layout.maxWidth;
    updateDesignSpec({ layoutStyle, layout: { ...current.layout, maxWidth } });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const updateColors = useCallback((colors: Partial<DesignSpec['colors']>) => {
    const current = designSpec ?? ensureDesignSpec();
    updateDesignSpec({ colors: { ...current.colors, ...colors } });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const updateTypography = useCallback((typography: Partial<DesignSpec['typography']>) => {
    const current = designSpec ?? ensureDesignSpec();
    updateDesignSpec({ typography: { ...current.typography, ...typography } });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const updateLayout = useCallback((layout: Partial<DesignSpec['layout']>) => {
    const current = designSpec ?? ensureDesignSpec();
    updateDesignSpec({ layout: { ...current.layout, ...layout } });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const updateSection = useCallback((updatedSection: SectionSpec) => {
    const current = designSpec ?? ensureDesignSpec();
    updateDesignSpec({
      sections: current.sections.map((s) => (s.id === updatedSection.id ? updatedSection : s)),
    });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const addSection = useCallback((type: SectionType) => {
    const current = designSpec ?? ensureDesignSpec();
    const nextOrder = current.sections.length > 0
      ? Math.max(...current.sections.map((s) => s.order)) + 1
      : 1;

    const newSection: SectionSpec = {
      id: `${type}-${generateId()}`,
      type,
      name: type.charAt(0).toUpperCase() + type.slice(1),
      order: nextOrder,
      layout: 'contained',
      content: {},
      visible: true,
    };

    updateDesignSpec({ sections: [...current.sections, newSection] });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const removeSection = useCallback((id: string) => {
    const current = designSpec ?? ensureDesignSpec();
    updateDesignSpec({
      sections: current.sections.filter((s) => s.id !== id),
    });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const moveSection = useCallback((id: string, direction: 'up' | 'down') => {
    const current = designSpec ?? ensureDesignSpec();
    const sorted = [...current.sections].sort((a, b) => a.order - b.order);
    const index = sorted.findIndex((s) => s.id === id);
    if (index === -1) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const temp = sorted[index].order;
    sorted[index].order = sorted[swapIndex].order;
    sorted[swapIndex].order = temp;

    updateDesignSpec({ sections: sorted });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  const toggleSectionVisibility = useCallback((id: string) => {
    const current = designSpec ?? ensureDesignSpec();
    updateDesignSpec({
      sections: current.sections.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)),
    });
  }, [designSpec, ensureDesignSpec, updateDesignSpec]);

  return {
    designSpec,
    ensureDesignSpec,
    setStyle,
    setColorScheme,
    setFontPair,
    setLayoutStyle,
    updateColors,
    updateTypography,
    updateLayout,
    updateSection,
    addSection,
    removeSection,
    moveSection,
    toggleSectionVisibility,
  };
}
